// shared/models/Project.ts

export enum ProjectCategory {
  GOVERNANCE = 'governance',
  POLICY_ADVOCACY = 'policy_advocacy',
  ENVIRONMENTAL = 'environmental',
  RESEARCH_DATA = 'research_data',
  PARTNERSHIPS = 'partnerships',
  CULTURAL_SHIFT = 'cultural_shift',
  REMEMBRANCE = 'remembrance'  // ⭐ NEW
}

export const ProjectCategoryLabels: Record<ProjectCategory, string> = {
  [ProjectCategory.GOVERNANCE]: 'Governance',
  [ProjectCategory.POLICY_ADVOCACY]: 'Policy Advocacy & Reform',
  [ProjectCategory.ENVIRONMENTAL]: 'Environmental Sustainability',
  [ProjectCategory.RESEARCH_DATA]: 'Research & Data Collection',
  [ProjectCategory.PARTNERSHIPS]: 'Partnership Building',
  [ProjectCategory.CULTURAL_SHIFT]: 'Cultural Paradigm Shift',
  [ProjectCategory.REMEMBRANCE]: 'Doctrine of Remembrance'
};

export const ProjectCategoryDescriptions: Record<ProjectCategory, string> = {
  [ProjectCategory.GOVERNANCE]: 'Promote transparency, accountability, and citizen participation in governance processes.',
  [ProjectCategory.POLICY_ADVOCACY]: 'Advance progressive socioeconomic policies addressing poverty, inequality, and social justice.',
  [ProjectCategory.ENVIRONMENTAL]: 'Promote conservation and sustainable practices for a healthy future for all.',
  [ProjectCategory.RESEARCH_DATA]: 'Generate evidence to inform policy and track impact across programmes.',
  [ProjectCategory.PARTNERSHIPS]: 'Work with government, private sector, and NGOs to amplify outcomes.',
  [ProjectCategory.CULTURAL_SHIFT]: 'Catalyze measurable shifts in Zambia\'s cultural paradigms.',
  [ProjectCategory.REMEMBRANCE]: 'Embed remembrance of Zambia\'s cultural heritage and global historical ruptures into civic education, public rituals, and national consciousness.'
};

export enum ProjectStatus {
  DRAFT = 'draft',
  PUBLISHED = 'published',
  ARCHIVED = 'archived',
  COMING_SOON = 'coming_soon'
}

export enum ProjectImpact {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical'
}

export interface ProjectImage {
  url: string;
  alt: string;
  caption?: string;
  isFeatured?: boolean;
}

export interface ProjectImpactMetric {
  label: string;
  value: string;
  description?: string;
}

export interface ProjectPhase {
  name: string;
  description: string;
  startDate: Date;
  endDate?: Date;
  isCompleted: boolean;
}

export interface ProjectPartner {
  name: string;
  logo?: string;
  url?: string;
  type: 'implementing' | 'funding' | 'collaborating';
}

export interface Project {
  _id?: string;
  
  // Basic Information
  title: string;
  slug: string;
  subtitle?: string;
  category: ProjectCategory;
  status: ProjectStatus;
  
  // Content
  description: string;
  fullDescription: string;
  objectives: string[];
  approach: string;
  expectedOutcomes: string[];
  
  // Visuals
  featuredImage: ProjectImage;
  gallery: ProjectImage[];
  
  // Impact & Metrics
  impact: ProjectImpact;
  metrics: ProjectImpactMetric[];
  
  // Timeline
  startDate: Date;
  endDate?: Date;
  phases: ProjectPhase[];
  isOngoing: boolean;
  
  // Partnerships
  partners: ProjectPartner[];
  
  // Location
  regions: string[];
  districts: string[];
  
  // Additional
  tags: string[];
  featured: boolean;
  views: number;
  
  // Metadata
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
  publishedAt?: Date;
  
  // SEO
  seoTitle?: string;
  seoDescription?: string;
  seoKeywords?: string[];
}

// Create Project DTO
export interface CreateProjectDTO {
  title: string;
  subtitle?: string;
  category: ProjectCategory;
  description: string;
  fullDescription: string;
  objectives: string[];
  approach: string;
  expectedOutcomes: string[];
  featuredImage: ProjectImage;
  gallery: ProjectImage[];
  impact: ProjectImpact;
  startDate: Date;
  endDate?: Date;
  isOngoing: boolean;
  regions: string[];
  districts: string[];
  tags: string[];
  featured: boolean;
  partners: ProjectPartner[];
  phases: ProjectPhase[];
  status?: ProjectStatus;
}

// Update Project DTO
export type UpdateProjectDTO = Partial<CreateProjectDTO> & {
  status?: ProjectStatus;
};

// Project Filters
export interface ProjectFilters {
  category?: ProjectCategory;
  status?: ProjectStatus;
  impact?: ProjectImpact;
  region?: string;
  featured?: boolean;
  search?: string;
  fromDate?: Date;
  toDate?: Date;
}

// Project Statistics
export interface ProjectStatistics {
  total: number;
  byCategory: Record<ProjectCategory, number>;
  byStatus: Record<ProjectStatus, number>;
  byImpact: Record<ProjectImpact, number>;
  featured: number;
}